import React, { useState, useEffect } from "react";
import { Club, IncreasedDataProtections } from "api/clubs";
import { CompanySubscriptionParams, getDefaultPaymentMethod, postCompanySubscriptons, putDefaultPaymentMethod, SubscriptionPlan } from "api/company";
import { CardElement, IbanElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElementChangeEvent, StripeCardElementOptions, StripeIbanElementChangeEvent } from "@stripe/stripe-js";
import { Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { GridTextField } from "components";
import i18n from "../i18n";
import "./subscriptionStyle.scss";

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
    hidePostalCode: true,
};

interface StripeCardInputProps {
    club: Club;
    noOfSeat: number;
    plan: SubscriptionPlan;
    closeMe: (showHide: boolean) => void;
    showSubscription: () => void;
    showLoader: (showHide: boolean) => void;
}

interface SEPADetailProps {
    name: string;
    email: string;
}

const IBAN_STYLE = {
    base: {
        color: "#707070",
        fontSize: "14px",
        "::placeholder": {
            color: "#707070",
        },
        ":-webkit-autofill": {
            color: "#707070",
        },
    },
    invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
        ":-webkit-autofill": {
            color: "#fa755a",
        },
    },
};

const IBAN_ELEMENT_OPTIONS = {
    supportedCountries: ["SEPA"],
    placeholderCountry: "DE",
    style: IBAN_STYLE,
};

const PAYMENT_METHOD_TYPES = {
    card: "Card",
    sepa: "SEPA",
    invoice: "Invoice",
};

const STRIPE_PAYMENT_METHOD_TYPES = {
    card: "card",
    sepa: "sepa_debit",
    invoice: "invoice",
};

const StripeCardInput = ({ club, noOfSeat, plan, closeMe, showSubscription, showLoader }: StripeCardInputProps) => {
    const [isValidCard, setIsValidCard] = useState(true);
    const [isEnableDefaultPayment, setIsEnableDefaultPayment] = useState(false);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<any>(null);
    const [paymentType, setPaymentType] = useState<string>(PAYMENT_METHOD_TYPES.card);
    const [sepaDetail, setSEPADetail] = useState<SEPADetailProps>({ name: "", email: "" });
    const [availableMethodTypes, setAvailableMethodTypes] = useState<string[]>([]);
    const [last4Digit, setLast4Digit] = useState<string>("");
    const stripe = useStripe();
    const elements = useElements();

    useEffect(() => {
        if (club) {
            getDefaultPaymentMethod({ clubId: club.id }).then((data) => {
                if (data) {
                    setDefaultPaymentMethod(data);
                    const last4 = data?.type === STRIPE_PAYMENT_METHOD_TYPES.card ? `**** **** **** ${data?.card?.last4}` : `**** **** **** **** **${data?.sepa_debit?.last4.match(/.{1,2}/g)?.join(" ")}`;
                    setLast4Digit(last4);
                }
            });
            const isInvoice = club?.increasedDataProtections?.includes(IncreasedDataProtections.INVOICE_METHOD);
            if (isInvoice) {
                setAvailableMethodTypes([PAYMENT_METHOD_TYPES.card, PAYMENT_METHOD_TYPES.sepa, PAYMENT_METHOD_TYPES.invoice]);
            } else {
                setAvailableMethodTypes([PAYMENT_METHOD_TYPES.card, PAYMENT_METHOD_TYPES.sepa]);
            }
        }
    }, [club]);

    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );
    const displayCustomError = (error: any) => enqueueSnackbar(
        `${i18n.insufficientNumberOfPurchasedSeatsMsg(error.associated)}`,
        { variant: "error" },
    );

    const onDefaultPayment = (value: boolean) => {
        setIsEnableDefaultPayment(value);
        onPaymentTypes(defaultPaymentMethod?.type);
    };

    const onPaymentTypes = (type: string) => {
        if (type === STRIPE_PAYMENT_METHOD_TYPES.card) {
            setPaymentType(PAYMENT_METHOD_TYPES.card);
        } else if (type === STRIPE_PAYMENT_METHOD_TYPES.sepa) {
            setPaymentType(PAYMENT_METHOD_TYPES.sepa);
        } else if (type === STRIPE_PAYMENT_METHOD_TYPES.invoice) {
            setPaymentType(PAYMENT_METHOD_TYPES.invoice);
        }
    };

    const validateStripeElement = (event: StripeCardElementChangeEvent) => {
        if (event.complete) {
            setIsValidCard(false);
        } else {
            setIsValidCard(true);
        }
    };

    const validateIbanElement = (event: StripeIbanElementChangeEvent) => {
        if (event.complete) {
            setIsValidCard(false);
        } else {
            setIsValidCard(true);
        }
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();

        showLoader(true);

        if (!elements || !stripe) {
            return;
        }

        let paymentMethodId = "";
        if (paymentType === PAYMENT_METHOD_TYPES.card) {
            if (defaultPaymentMethod?.type === STRIPE_PAYMENT_METHOD_TYPES.card && defaultPaymentMethod?.id) {
                paymentMethodId = defaultPaymentMethod.id;
            } else {
                const card = elements.getElement(CardElement);
                if (card) {
                    const { error, paymentMethod } = await stripe.createPaymentMethod({
                        type: "card",
                        card,
                    });
                    if (error) {
                        enqueueSnackbar(
                            `${i18n.anErrorOccurred}: ${error.message}`,
                            { variant: "error" },
                        );
                        showLoader(false);
                        closeMe(false);
                        return;
                    }
                    if (paymentMethod) {
                        paymentMethodId = paymentMethod.id;
                    }
                }
            }
        } else if (paymentType === PAYMENT_METHOD_TYPES.sepa) {
            if (defaultPaymentMethod?.type === STRIPE_PAYMENT_METHOD_TYPES.sepa && defaultPaymentMethod?.id) {
                paymentMethodId = defaultPaymentMethod.id;
            } else {
                const Iban = elements.getElement(IbanElement);
                if (Iban) {
                    const { error, paymentMethod } = await stripe.createPaymentMethod({
                        type: "sepa_debit",
                        sepa_debit: Iban,
                        billing_details: {
                            email: sepaDetail?.email,
                            name: sepaDetail?.name,
                        },
                    });
                    if (error) {
                        enqueueSnackbar(
                            `${i18n.anErrorOccurred}: ${error.message}`,
                            { variant: "error" },
                        );
                        showLoader(false);
                        closeMe(false);
                        return;
                    }
                    if (paymentMethod) {
                        paymentMethodId = paymentMethod.id;
                    }
                }
            }
        }
        let params: CompanySubscriptionParams = {
            clubId: "",
            stripePriceId: "",
            numberOfSeats: 0,
            paymentMethodId: "",
        };
        if (paymentMethodId && paymentType !== PAYMENT_METHOD_TYPES.invoice) {
            params = {
                clubId: club.id,
                stripePriceId: plan.stripePriceId,
                numberOfSeats: noOfSeat,
                paymentMethodId,
            };
        } else if (paymentType === PAYMENT_METHOD_TYPES.invoice) {
            params = {
                clubId: club.id,
                stripePriceId: plan.stripePriceId,
                numberOfSeats: noOfSeat,
                payManuallyViaInvoice: true,
            };
        }

        try {
            const subscriptionDetails = await postCompanySubscriptons(params);
            if (subscriptionDetails) {
                enqueueSnackbar(
                    `${i18n.subscriptionSuccessMsg} (${i18n.subscriptionSuccessDetailMsg(noOfSeat, plan)})`,
                    { variant: "success" },
                );
                closeMe(false);
                showSubscription();
                if (paymentMethodId !== defaultPaymentMethod?.id && paymentType !== PAYMENT_METHOD_TYPES.invoice) {
                    await putDefaultPaymentMethod({ clubId: club.id, paymentMethodId });
                }
            }
        } catch (err: any) {
            if (err.type === "insufficientNumberOfPurchasedSeats") {
                displayCustomError(err);
            } else {
                displayError(err);
            }
        } finally {
            showLoader(false);
        }
    };

    const sepaCheck = (isValidCard || (sepaDetail?.name?.length === 0 || sepaDetail?.email?.length === 0));
    const disable = (noOfSeat === 0) || (paymentType !== PAYMENT_METHOD_TYPES.invoice && (paymentType === PAYMENT_METHOD_TYPES.sepa ? sepaCheck : isValidCard) && !isEnableDefaultPayment);
    return (
        <div>
            <form onSubmit={handleSubmit} className="payment_form">
                {defaultPaymentMethod && (
                    <Grid>
                        <FormControl>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={isEnableDefaultPayment}
                                        color="primary"
                                        onChange={(e) => onDefaultPayment(e.target.checked)}
                                    />
                                )}
                                label={i18n.selectDefaultPaymentMethod}
                            />
                        </FormControl>
                    </Grid>
                )}
                {isEnableDefaultPayment && (
                    <div style={{ flexDirection: "row" }}>
                        <Typography variant="inherit">{i18n.cardNumber}</Typography>
                        <Typography className="last_digit" variant="inherit">{last4Digit}</Typography>
                    </div>
                )}
                <FormControl disabled={isEnableDefaultPayment} className="method_Selection" variant="outlined">
                    <InputLabel>{i18n.paymentType}</InputLabel>
                    <Select
                        label={i18n.paymentType}
                        value={paymentType}
                        onChange={(e) => setPaymentType(e.target.value as typeof paymentType)}
                    >
                        {availableMethodTypes.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {!isEnableDefaultPayment && paymentType === PAYMENT_METHOD_TYPES.card && (
                    <div className="card_element">
                        <CardElement onChange={validateStripeElement} options={CARD_ELEMENT_OPTIONS} />
                    </div>
                )}
                {!isEnableDefaultPayment && paymentType === PAYMENT_METHOD_TYPES.sepa && (
                    <div>
                        <div className="iban_element">
                            <IbanElement onChange={validateIbanElement} options={IBAN_ELEMENT_OPTIONS} />
                        </div>
                        <Grid container spacing={2}>
                            <GridTextField
                                label={i18n.name}
                                size={6}
                                value={sepaDetail?.name}
                                onChange={(text) => setSEPADetail({ ...sepaDetail, name: text })}
                            />
                            <GridTextField
                                label={i18n.clubEmail}
                                size={6}
                                value={sepaDetail?.email}
                                onChange={(text) => setSEPADetail({ ...sepaDetail, email: text })}
                            />
                        </Grid>
                    </div>
                )}
                <div className="subscribe_btn">
                    <Button type="submit" color="primary" disabled={disable} className="payment_form">
                        {i18n.subscribe}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default StripeCardInput;
